import { supabase } from './supabase';

const SUPABASE_URL = import.meta.env?.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env?.VITE_SUPABASE_ANON_KEY;

/**
 * Get current user's access token for authenticated edge function calls.
 */
async function getAuthToken() {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.access_token || '';
}

/**
 * Legacy chat mode — sends user messages only (system prompts are rebuilt server-side).
 */
export async function callAI({ messages, platforms }) {
  const token = await getAuthToken();
  const url = `${SUPABASE_URL}/functions/v1/ai-generate`;

  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      'apikey': SUPABASE_ANON_KEY,
    },
    body: JSON.stringify({
      action: 'chat',
      messages,
      platforms,
    }),
  });

  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    const status = res.status;
    if (status === 401) throw Object.assign(new Error('Session expired. Please sign in again.'), { type: 'AuthenticationError' });
    if (status === 402 || (status === 403 && errorData?.error === 'limit_reached')) {
      throw Object.assign(new Error(errorData.message || 'Usage limit reached'), {
        type: 'LimitReached',
        used: errorData.used,
        limit: errorData.limit,
        daysUntilReset: errorData.daysUntilReset,
      });
    }
    if (status === 403) throw Object.assign(new Error('Permission denied'), { type: 'PermissionDeniedError' });
    if (status === 429) throw Object.assign(new Error('Too many requests'), { type: 'RateLimitError' });
    if (status >= 500) throw Object.assign(new Error('Service unavailable'), { type: 'InternalServerError' });
    throw new Error(errorData?.error || 'Request failed');
  }

  return res.json();
}

/**
 * Secure generate mode — sends only raw data, prompts built server-side.
 */
export async function callAIGenerate({ roughIdea, codeSnippet, platforms, styleDetails, additionalInstructions, githubUrl }) {
  const token = await getAuthToken();
  const url = `${SUPABASE_URL}/functions/v1/ai-generate`;

  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      'apikey': SUPABASE_ANON_KEY,
    },
    body: JSON.stringify({
      action: 'generate',
      roughIdea,
      codeSnippet,
      platforms,
      styleDetails,
      additionalInstructions,
      githubUrl,
    }),
  });

  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    const status = res.status;
    if (status === 401) throw Object.assign(new Error('Session expired. Please sign in again.'), { type: 'AuthenticationError' });
    if (status === 422 && errorData?.error === 'input_quality') {
      throw Object.assign(new Error(errorData.message || 'Input doesn\'t seem like a post idea.'), {
        type: 'InputQuality',
        score: errorData.score,
        aiFlagged: errorData.ai_flagged || false,
      });
    }
    if (status === 402 || (status === 403 && errorData?.error === 'limit_reached')) {
      throw Object.assign(new Error(errorData.message || 'Usage limit reached'), {
        type: 'LimitReached',
        used: errorData.used,
        limit: errorData.limit,
        daysUntilReset: errorData.daysUntilReset,
      });
    }
    if (status === 403) throw Object.assign(new Error('Permission denied'), { type: 'PermissionDeniedError' });
    if (status === 429) throw Object.assign(new Error('Too many requests'), { type: 'RateLimitError' });
    if (status >= 500) throw Object.assign(new Error('Service unavailable'), { type: 'InternalServerError' });
    throw new Error(errorData?.error || 'Request failed');
  }

  return res.json();
}
